import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import './image-text.scss';

// Regular imports for pre-rendering compatibility
import OptimizedImage from '../common/OptimizedImage';
import NewsCarousel from '../carousel/NewsCarousel';

/**
 * A flexible image-text component that can display images on either the left or right side.
 * 
 * @param {Object} props
 * @param {string} props.title - The main heading for the component
 * @param {string} props.subtitle - Optional subtitle text
 * @param {string} props.content - Main content text, can include HTML markup
 * @param {string} props.imageSrc - Path to the image
 * @param {string} props.imageAlt - Alt text for the image
 * @param {string} props.buttonText - Optional button text
 * @param {string} props.buttonLink2 - Optional button link URL
 * @param {string} props.buttonText2 - Optional button text
 * @param {string} props.buttonLink - Optional button link URL
 * @param {boolean} props.imageOnRight - Whether to display the image on the right (true) or left (false)
 * @param {string} props.backgroundColor - Optional background color (default: white)
 * @param {string} props.textColor - Optional text color (default: inherits from parent)
 * @param {string} props.gradientTitle - Optional gradient style for title: 'green', 'red-to-green', 'green-to-red', or none
 * @param {boolean} props.useHeaderBackground - Whether to use a header-style SVG background instead of an image
 * @param {boolean} props.fullWidthBackground - Whether to extend the background to the full width of the viewport
 * @param {string} props.backgroundClassName - Additional CSS classes to apply to the component
 * @param {boolean} props.useCarousel - Whether to display a carousel instead of an image
 * @param {Array} props.carouselItems - Array of news items for the carousel (if useCarousel is true)
 * @returns {JSX.Element}
 */
const ImageText = ({
  title,
  subtitle,
  content,
  imageSrc,
  imageAlt,
  buttonText,
  buttonLink,
  buttonText2,
  buttonLink2,
  imageOnRight = false,
  backgroundColor = 'white',
  textColor,
  gradientTitle,
  useHeaderBackground = false,
  fullWidthBackground = true,
  className = '',
  backgroundClassName = 'header-style-background-training',
  subjectType = '', // New prop for contact subject
  useCarousel = false, // New prop to enable carousel mode
  carouselItems = [] // New prop for carousel items
}) => {// Determine title class based on gradient option
  const getTitleClass = () => {
    switch (gradientTitle) {
      case 'green':
        return 'green-gradient-font';
      case 'red-to-green':
        return 'red-to-green-gradient-font';
      case 'green-to-red':
        return 'green-to-red-gradient-font';
      default:
        return '';
    }
  };  // Function to handle scrolling to contact form and setting subject
  const scrollToContactWithSubject = (e, subject) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event propagation to prevent global handlers from intercepting
    
    // Determine subject based on context or parameter
    const contactSubject = subject || subjectType || 'General Inquiry';
    console.log("Scrolling to contact with subject:", contactSubject);
    
    // Scroll to the contact section
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      // Use direct scrollIntoView call with a lower-level approach
      try {
        // First attempt - use scrollIntoView
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        
        // Second attempt - use element.offset approach after a short delay
        setTimeout(() => {
          const yOffset = contactSection.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: yOffset, behavior: 'smooth' });
            // Set the hash parameter for the subject
          setTimeout(() => {
            console.log("Setting hash to:", `subject=${contactSubject.toLowerCase()}`);
            window.history.replaceState(null, null, `#subject=${contactSubject.toLowerCase()}`);
          }, 800);
        }, 50);
      } catch (error) {
        console.error("Error scrolling to contact section:", error);      }
    }
  };
  return (
    <div
      className={`image-text-component ${imageOnRight ? 'image-right' : 'image-left'} ${fullWidthBackground ? 'full-width-bg' : ''} ${className}`}
      style={{
        backgroundColor: fullWidthBackground ? 'transparent' : backgroundColor,
        color: textColor
      }}
    >
      <div className="content-container">
        <div className="text-content">
          {title && <h2 className={getTitleClass()}>{title}</h2>}
          {subtitle && <h5>{subtitle}</h5>}

          {/* Use dangerouslySetInnerHTML to allow HTML in content */}
          {content && (
            <div
              className="main-content"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}          <div className="button-container">
            {buttonText && buttonLink && (
              <Link to={buttonLink} className="button-link">
                <button className="btn btn-primary">{buttonText}</button>
              </Link>
            )}
            {buttonText2 && buttonLink2 && buttonLink2 === '/contact' ? (
              <div className="button-link">
                <button
                  className="btn btn-secondary"
                  onClick={(e) => scrollToContactWithSubject(e, subjectType)}
                >
                  {buttonText2}
                </button>
              </div>
            ) : buttonText2 && buttonLink2 && (
              <Link to={buttonLink2} className="button-link">
                <button className="btn btn-secondary">{buttonText2}</button>
              </Link>
            )}
          </div>        </div>
        <div className="image-container">
          {useCarousel ? (
            <Suspense fallback={<div>Loading carousel...</div>}>
              <NewsCarousel newsItems={carouselItems} />
            </Suspense>
          ) : useHeaderBackground ? (
            <div className={backgroundClassName} aria-label={imageAlt || 'Feature background'} />
          ) : (
            <Suspense fallback={<div>Loading image...</div>}>
              <OptimizedImage src={imageSrc} alt={imageAlt || 'Feature image'} className="img-fluid" />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

ImageText.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  content: PropTypes.string.isRequired,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
  buttonText: PropTypes.string,
  buttonLink: PropTypes.string,
  fullWidthBackground: PropTypes.bool,
  imageOnRight: PropTypes.bool,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  gradientTitle: PropTypes.oneOf(['green', 'red-to-green', 'green-to-red']),
  useHeaderBackground: PropTypes.bool,
  className: PropTypes.string,
  useCarousel: PropTypes.bool,
  carouselItems: PropTypes.array
};

export default ImageText;
